import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, ArrowLeft, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Status — public trust page at /status showing live health of API,
 * database, LiveKit, Panda AI, and Object Storage. No auth. Polls every
 * 30s. Uses only the public /api/status endpoint (never leaks internals).
 */
export default function Status() {
  const [state, setState] = useState({ loading: true, ok: null, components: [], checked_at: null });
  const [lastFetched, setLastFetched] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/status`);
      setState({ loading: false, ok: !!data.ok, components: data.components || [], checked_at: data.checked_at });
      setLastFetched(new Date());
    } catch {
      setState({ loading: false, ok: false, components: [], checked_at: null });
      setLastFetched(new Date());
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const overallLabel = state.loading
    ? "Checking…"
    : state.ok
      ? "All systems operational"
      : "Partial outage";
  const overallColor = state.loading
    ? "text-zinc-400 border-white/10"
    : state.ok
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-950/20"
      : "text-rose-300 border-rose-500/40 bg-rose-950/20";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="status-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <Link to="/" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-rose-500 inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={12} /> Back to My Panda X
        </Link>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.35em] text-rose-500 mb-2">System status</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            Is My Panda X <span className="text-rose-400">up?</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
            Live health of every core component powering the app. Refreshes every 30 seconds.
          </p>
        </div>

        <div
          data-testid="status-overall"
          className={`border ${overallColor} px-6 py-5 mb-6 flex items-center justify-between`}
        >
          <div className="flex items-center gap-3">
            {state.loading ? (
              <RefreshCw size={22} className="animate-spin" />
            ) : state.ok ? (
              <CheckCircle2 size={22} />
            ) : (
              <XCircle size={22} />
            )}
            <div>
              <div className="font-display text-xl leading-tight" data-testid="status-overall-label">
                {overallLabel}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Last checked{" "}
                {lastFetched
                  ? lastFetched.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                  : "…"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="status-refresh"
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1.5"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        <div className="space-y-2" data-testid="status-components">
          {state.components.map((c) => (
            <div
              key={c.key}
              data-testid={`status-component-${c.key}`}
              className="border border-white/10 bg-white/[0.02] px-5 py-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {c.ok ? (
                  <CheckCircle2 size={18} className="text-emerald-400" />
                ) : (
                  <XCircle size={18} className="text-rose-400" />
                )}
                <div>
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {c.ok ? "Operational" : "Degraded"}
                  </div>
                </div>
              </div>
              {c.latency_ms != null && (
                <div className="text-xs text-zinc-500 flex items-center gap-1">
                  <Clock size={11} /> {c.latency_ms} ms
                </div>
              )}
            </div>
          ))}
          {state.components.length === 0 && !state.loading && (
            <div className="border border-rose-500/40 bg-rose-950/20 px-5 py-6 text-sm text-rose-300">
              Couldn't reach the status endpoint. That itself is a sign of an outage — try again in a minute.
            </div>
          )}
        </div>

        <div className="mt-12 border-t border-white/5 pt-6 text-[11px] text-zinc-500 leading-relaxed">
          Not seeing what you expected? DM <span className="text-rose-400">@panda</span> from{" "}
          <Link to="/inbox" className="text-rose-400 hover:underline">/inbox</Link> or check the{" "}
          <Link to="/faq/members" className="text-rose-400 hover:underline">member FAQ</Link>.
        </div>
      </div>
    </div>
  );
}
